import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import TextField from '@mui/material/TextField';
import { Course } from '../../../../types/Course';
import { Institution } from '../../../../types/Institution';
import { InstitutionTypeFilter } from '../../../../services/adapters/queries/institutionsQueryFunction';
import { verticalCopy } from '../../../../utils/constants/verticalCopy';
import { normalizeText } from '../../../../utils/functions/normalizeText';
import './EditCoursesSelector.css';

type ParticipationType = Exclude<InstitutionTypeFilter, 'all'>;

interface CoursesSelectorProps {
  institutions: Institution[];
  institutionTypes: ParticipationType[];
  coursesByInstitution: Map<string, Course[]>;
  dictatedCourses: Course[];
  onChange: (updatedCourses: Course[]) => void;
}

interface InstitutionGroup {
  type: ParticipationType;
  institutions: Institution[];
}

const blacklistedInstitutions = [
  'itba',
  'fceAustral',
  'fcbiomedAustral',
  'fiAustral',
  'ingresoAustral',
  'utdt',
  'ingresoUtdt',
  'udesa',
  'ingresoUdesa'
];

const EditCoursesSelector: React.FC<CoursesSelectorProps> = ({
  institutions,
  institutionTypes,
  coursesByInstitution,
  dictatedCourses,
  onChange
}) => {
  const [updatedCourses, setUpdatedCourses] = useState<Course[]>(dictatedCourses);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedTypes, setExpandedTypes] = useState<string[]>([]);
  const [expandedInstitutions, setExpandedInstitutions] = useState<string[]>([]);

  const search = normalizeText(searchTerm.trim());
  const isSearching = search.length > 0;

  // Materias visibles por institución: con búsqueda activa se filtran por nombre de
  // materia, salvo que la institución en sí matchee (ahí se muestran todas).
  const visibleCourses = useMemo<Map<string, Course[]>>(() => {
    const entries = institutions.map((institution): [string, Course[]] => {
      const courses = coursesByInstitution.get(institution.value) ?? [];

      if (!search || normalizeText(institution.label).includes(search)) {
        return [institution.value, courses];
      }

      return [
        institution.value,
        courses.filter((course) => normalizeText(course.label).includes(search))
      ];
    });

    return new Map(entries);
  }, [institutions, coursesByInstitution, search]);

  const groups = useMemo<InstitutionGroup[]>(() => {
    const groupedInstitutions = institutionTypes.map((type) => ({
      type,
      institutions: institutions.filter(
        (institution) =>
          institution.type === type &&
          (!isSearching || (visibleCourses.get(institution.value)?.length ?? 0) > 0)
      )
    }));

    return groupedInstitutions.filter((group) => group.institutions.length > 0);
  }, [institutionTypes, institutions, isSearching, visibleCourses]);

  // Los contadores se calculan sobre todas las materias elegidas (no sobre las
  // filtradas) para que no cambien mientras se busca.
  const selectedCounts = useMemo(() => {
    const byInstitution = new Map<string, number>();
    const byType = new Map<string, number>();

    updatedCourses.forEach((course) => {
      const { value: institutionValue, type } = course.institution ?? {};

      if (institutionValue) {
        byInstitution.set(institutionValue, (byInstitution.get(institutionValue) ?? 0) + 1);
      }

      if (type) {
        byType.set(type, (byType.get(type) ?? 0) + 1);
      }
    });

    return { byInstitution, byType };
  }, [updatedCourses]);

  const handleCourseSelection = (course: Course, isSelected: boolean) => {
    const newCourses = isSelected
      ? [...updatedCourses.filter((c) => c.value !== course.value), course]
      : updatedCourses.filter((c) => c.value !== course.value);

    setUpdatedCourses(newCourses);
    onChange(newCourses);
  };

  const toggleExpanded = (values: string[], value: string, isExpanded: boolean): string[] =>
    isExpanded ? [...values, value] : values.filter((v) => v !== value);

  const emptyMessage = (): string => {
    if (isSearching) {
      return `No encontramos materias que coincidan con "${searchTerm.trim()}".`;
    }

    if (institutionTypes.length === 0) {
      return 'Seleccioná al menos una vertical para elegir las materias que dictás.';
    }

    return 'Todavía no hay instituciones disponibles para las verticales que elegiste.';
  };

  return (
    <div className="editCoursesSelector">
      <TextField
        placeholder="Buscar materia o institución"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        size="small"
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: searchTerm ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="Limpiar búsqueda"
                size="small"
                onClick={() => setSearchTerm('')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined
        }}
      />

      {groups.length === 0 ? (
        <Typography className="coursesEmptyMessage">{emptyMessage()}</Typography>
      ) : (
        groups.map((group) => {
          const selectedInGroup = selectedCounts.byType.get(group.type);

          return (
            <Accordion
              key={group.type}
              disableGutters
              className="coursesTypeAccordion"
              TransitionProps={{ unmountOnExit: true }}
              expanded={isSearching || expandedTypes.includes(group.type)}
              onChange={(_event, isExpanded) =>
                setExpandedTypes((prev) => toggleExpanded(prev, group.type, isExpanded))
              }>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <div className="coursesAccordionSummary">
                  <Typography className="coursesTypeTitle">
                    {verticalCopy[group.type].contextLabel}
                  </Typography>
                  {!!selectedInGroup && (
                    <Chip size="small" color="primary" label={selectedInGroup} />
                  )}
                </div>
              </AccordionSummary>
              <AccordionDetails className="coursesTypeAccordionDetails">
                {group.institutions.map((institution) => {
                  const isBlacklisted = blacklistedInstitutions.includes(institution.value);
                  const courses = visibleCourses.get(institution.value) ?? [];
                  const selectedInInstitution = selectedCounts.byInstitution.get(institution.value);

                  return (
                    <Accordion
                      key={institution.value}
                      disableGutters
                      className="coursesInstitutionAccordion"
                      TransitionProps={{ unmountOnExit: true }}
                      expanded={isSearching || expandedInstitutions.includes(institution.value)}
                      onChange={(_event, isExpanded) =>
                        setExpandedInstitutions((prev) =>
                          toggleExpanded(prev, institution.value, isExpanded)
                        )
                      }>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <div className="coursesAccordionSummary">
                          <Typography className="coursesInstitutionTitle">
                            {institution.label}
                          </Typography>
                          {!!selectedInInstitution && (
                            <Chip size="small" color="primary" label={selectedInInstitution} />
                          )}
                        </div>
                      </AccordionSummary>
                      <AccordionDetails className="coursesInstitutionAccordionDetails">
                        {isBlacklisted && (
                          <Typography className="coursesBlacklistNote">
                            Si querés hacer cambios de materias contactanos!
                          </Typography>
                        )}
                        {courses.length > 0 ? (
                          courses.map((course) => {
                            const isCourseSelected = updatedCourses.some(
                              (c) => c.value === course.value
                            );

                            return (
                              <div className="courseRow" key={course.value}>
                                <FormControlLabel
                                  className="courseRowLabel"
                                  control={
                                    <Checkbox
                                      checked={isCourseSelected}
                                      onChange={(e) =>
                                        handleCourseSelection(course, e.target.checked)
                                      }
                                      disabled={isBlacklisted}
                                    />
                                  }
                                  label={course.label}
                                />
                                {isCourseSelected && (
                                  <TextField
                                    placeholder="Notas adicionales (opcional)"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    defaultValue={
                                      updatedCourses.find((c) => c.value === course.value)?.notes ||
                                      ''
                                    }
                                    onChange={(e) =>
                                      handleCourseSelection(
                                        { ...course, notes: e.target.value },
                                        true
                                      )
                                    }
                                    disabled={isBlacklisted}
                                  />
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <Typography className="coursesEmptyMessage">
                            No hay materias disponibles para esta institución.
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          );
        })
      )}
    </div>
  );
};

export default EditCoursesSelector;
